/**
 * Firebase configuration — SmartLawyer
 * Utilisé pour l'auth publique (avocats/abonnés)
 * Séparé de l'auth Chainlit (PostgreSQL)
 */

import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY            || 'AIzaSyCHriUio0W75H2dmSTdlLoexkNwjwVllNw',
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN        || 'smartlawyer-388911.firebaseapp.com',
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID         || 'smartlawyer-388911',
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET     || 'smartlawyer-388911.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '1037889742287',
  appId:             import.meta.env.VITE_FIREBASE_APP_ID             || '1:1037889742287:web:189652dc5ad105da86deba',
};

// Init Firebase une seule fois
const app  = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
export const auth = getAuth(app);

// ── Providers ─────────────────────────────────────────────────────────────────

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.setCustomParameters({
  tenant: import.meta.env.VITE_AZURE_AD_TENANT_ID || '1bff5d9a-a4dd-4972-bb01-663bbf865481',
  prompt: 'select_account',
});

// ── Auth functions ────────────────────────────────────────────────────────────

export const signInWithGoogle = () =>
  signInWithPopup(auth, googleProvider);

export const signInWithMicrosoft = () =>
  signInWithPopup(auth, microsoftProvider);

export const signInWithEmail = (email: string, password: string) =>
  signInWithEmailAndPassword(auth, email, password);

export const signUpWithEmail = (email: string, password: string) =>
  createUserWithEmailAndPassword(auth, email, password);

export const signOutUser = () => signOut(auth);

// ── Auth de test E2E ─────────────────────────────────────────────────────────
// Uniquement sous `vite dev` avec VITE_TEST_AUTH=1 (éliminé des builds de prod
// par import.meta.env.DEV) : Playwright dépose { id, name } dans
// localStorage["sl_test_user"] et le backend (TRIALS_TEST_AUTH=1) accepte le
// jeton « test:e2e-<id>:<nom> » à la place d'un ID token Firebase.

const TEST_AUTH = import.meta.env.DEV && import.meta.env.VITE_TEST_AUTH === '1';

function getTestUser(): User | null {
  if (!TEST_AUTH) return null;
  try {
    const raw = localStorage.getItem('sl_test_user');
    if (!raw) return null;
    const { id, name } = JSON.parse(raw) as { id: string; name: string };
    const uid = `e2e-${id}`;
    return {
      uid,
      displayName: name,
      email: `${uid}@e2e.test`,
      photoURL: null,
      getIdToken: async () => `test:${uid}:${encodeURIComponent(name)}`,
    } as unknown as User;
  } catch {
    return null;
  }
}

export const onAuthChange = (callback: (user: User | null) => void) => {
  const testUser = getTestUser();
  if (testUser) {
    callback(testUser);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};

// ── Get ID token pour les appels API ─────────────────────────────────────────

export const getIdToken = async (): Promise<string | null> => {
  const user = getTestUser() ?? auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
};

export type { User };